import { useEffect, useState, type CSSProperties } from 'react';
import { api } from '../../lib/api';
import { ComboBox, type OpcaoCombo } from '../../components/ComboBox';
import { Trash2, Plus, Star } from 'lucide-react';

// Onde o produto mora (DA-324). Substitui o `input type=text` de texto livre do ProdutoForm.
// E AQUI que a faxina das localizacoes acontece e que as principais sao marcadas.
export interface LocalProduto {
  localizacao_id: number;
  principal: boolean;
  quantidade: string | null;
  quantidade_minima: string | null;
  codigo?: string;
  descricao?: string | null;
  ativo?: boolean;
}

interface Props {
  valor: LocalProduto[];
  onChange: (v: LocalProduto[]) => void;
  somenteLeitura?: boolean;
  /**
   * `Minimo de Compra` do PRODUTO. Prateleira sem minimo proprio HERDA este numero ([DA-340]) —
   * aparece como sugestao no campo vazio em vez do `—`. Herda, nao copia: o campo continua vazio no
   * banco, entao mudar o minimo do produto reflete em todas as prateleiras que nao escolheram o seu.
   */
  minimoHerdado?: string | number | null;
}

// Medido na tela real (coluna do ProdutoForm, viewport do Ruan): com 58px os dois campos + estrela +
// codigo + lixeira cabem com estouro ZERO. Nao aumentar sem medir de novo.
const LARGURA_CAMPO = 58;

const ESTILO_CAMPO: CSSProperties = {
  width: LARGURA_CAMPO,
  textAlign: 'right',
  fontSize: 12,
  padding: '1px 4px',
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  color: 'var(--text)',
  borderRadius: 4,
  fontFamily: '"IBM Plex Mono", monospace',
};

export function LocalizacoesProduto({ valor, onChange, somenteLeitura = false, minimoHerdado }: Props) {
  const [opcoes, setOpcoes] = useState<OpcaoCombo[]>([]);
  const [adicionando, setAdicionando] = useState<string | number | ''>('');

  // O que o campo vazio de minimo MOSTRA. Zero nao e minimo — e "ninguem definiu" no produto
  // tambem (DA-332), entao nesse caso segue o `—` em vez de sugerir um alarme que nunca dispara.
  const nHerdado = Number(minimoHerdado ?? 0);
  const dicaMinimo = Number.isFinite(nHerdado) && nHerdado > 0 ? String(nHerdado).replace('.', ',') : '—';

  useEffect(() => {
    void (async () => {
      try {
        const lista = await api.get<{ id: number; codigo: string; descricao: string | null }[]>('/api/localizacoes');
        setOpcoes(lista.map((l) => ({ value: l.id, label: l.descricao ? `${l.codigo} — ${l.descricao}` : l.codigo })));
      } catch { /* lista vazia: o combo so nao oferece nada; o form segue salvavel */ }
    })();
  }, []);

  const usados = new Set(valor.map((v) => v.localizacao_id));
  // Os lugares que o produto JA tem seguem na lista, cinza e nao clicaveis — antes eram filtrados
  // fora, e sumir era pior do que parece: o combo casa o texto em qualquer posicao do rotulo, entao
  // digitar "A" trazia BROCAS/BALCAO/BANHEIRO e escondia justamente o "A". O Ruan concluiu, duas
  // vezes, que a prateleira nao existia no cadastro. Duplicar continua impossivel; o "ja esta aqui"
  // e que passou a existir.
  const listaCombo = opcoes.map((o) => (usados.has(Number(o.value))
    ? { ...o, desabilitada: true, nota: 'ja esta aqui' }
    : o));
  const temEscolhivel = listaCombo.some((o) => !o.desabilitada);

  function adicionar(id: string | number | '') {
    if (id === '' || id === null) return;
    const num = Number(id);
    if (usados.has(num)) return;
    const opt = opcoes.find((o) => Number(o.value) === num);
    // Adicionar um SEGUNDO lugar NAO desmarca o primeiro: o erro so aparece quando ele e real
    // (DA-324). Se este e o unico, ele ja e a principal — nao ha o que perguntar.
    onChange([...valor, {
      localizacao_id: num,
      principal: valor.length === 0,
      quantidade: null,
      quantidade_minima: null,
      codigo: opt?.label.split(' — ')[0],
    }]);
    setAdicionando('');
  }

  function remover(id: number) {
    const resto = valor.filter((v) => v.localizacao_id !== id);
    // Sobrou um so? Ele vira a principal sozinho — senao o produto ficaria com 1 lugar e nenhuma
    // marcada, e o Salvar travaria por um estado que o proprio sistema criou.
    onChange(resto.length === 1 ? resto.map((v) => ({ ...v, principal: true })) : resto);
  }

  function marcarPrincipal(id: number) {
    onChange(valor.map((v) => ({ ...v, principal: v.localizacao_id === id })));
  }

  const semPrincipal = valor.length >= 2 && !valor.some((v) => v.principal);

  // Quantidade por lugar: `null` = NUNCA CONTADO (mostra `—`), `'0'` = contado e vazio. So o zero
  // dispara reposicao — o traco fica quieto. O campo nasce vazio em vez de herdar o total porque
  // numero fabricado e indistinguivel de numero contado, e repartir um total entre VARIAS
  // prateleiras seria chute.
  //
  // EXCECAO, e ela nao contradiz o de cima: com UM lugar so nao ha o que repartir nem onde mais a
  // mercadoria poderia estar, entao a contagem dele e o proprio Est. Atual do produto ([DA-366]).
  // Nesse caso o pai espelha os dois campos (ver `aplicarLocais` no ProdutoForm) e o backend grava
  // o total sozinho se a tela nao mandar numero.
  function editar(id: number, campo: 'quantidade' | 'quantidade_minima', v: string) {
    onChange(valor.map((x) => (x.localizacao_id === id ? { ...x, [campo]: v === '' ? null : v } : x)));
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Localizacao Fisica</label>

      {valor.length === 0 ? (
        <p className="text-sm text-gray-400 mb-2">Nenhum lugar. O produto nao aparece na ronda do estoquista.</p>
      ) : (
        <div className="flex flex-col gap-1 mb-2">
          {/* Os MESMOS rotulos do rodape de proposito: e o mesmo conceito ("quanto tem" / "quando
              repor") num escopo menor, e o bloco "Localizacao Fisica" ja diz qual escopo e. O que
              NAO repete o nome do rodape e o `Minimo de Compra` — la a pergunta e "quando comprar?",
              aqui e "quando buscar no deposito?". Sinais diferentes, nomes diferentes (DA-324). */}
          {/* 21px = a lixeira (13) + o marginLeft (4) + folga. Sem lixeira (modo leitura) o cabecalho
              nao pode reservar esse espaco, senao desalinha dos campos. */}
          <div className="flex items-center gap-2" style={{ paddingRight: somenteLeitura ? 0 : 21, fontSize: 10, color: 'var(--text-muted)' }}>
            <div style={{ flex: 1 }} />
            <span style={{ width: LARGURA_CAMPO, textAlign: 'right' }}>Est. Atual</span>
            <span style={{ width: LARGURA_CAMPO, textAlign: 'right' }}>Est. Minimo</span>
          </div>
          {valor.map((v) => (
            <div key={v.localizacao_id} className="flex items-center gap-2 text-sm">
              {/* A estrela marca de onde a venda baixa. Amarelo `#fde68a` + preto = o positivo do
                  sistema (o Ruan nao distingue verde/vermelho). */}
              <button
                type="button"
                title={v.principal ? 'Principal — a venda baixa daqui' : 'Marcar como principal'}
                disabled={somenteLeitura}
                onClick={() => marcarPrincipal(v.localizacao_id)}
                style={{
                  background: v.principal ? '#fde68a' : 'transparent',
                  color: v.principal ? '#000' : 'var(--text-muted)',
                  border: '1px solid', borderColor: v.principal ? '#fde68a' : 'var(--input-border)',
                  borderRadius: 4, padding: '2px 5px', lineHeight: 0,
                  cursor: somenteLeitura ? 'default' : 'pointer',
                }}
              >
                <Star size={13} fill={v.principal ? '#000' : 'none'} />
              </button>
              <span style={{ fontWeight: 600 }}>{v.codigo ?? `#${v.localizacao_id}`}</span>
              {v.ativo === false && <span className="text-xs text-gray-400">(inativa)</span>}
              {/* O "a venda baixa daqui" saiu da linha pra abrir espaco pros dois campos — virou o
                  title da estrela. Medido: com ele a linha estourava a coluna. */}
              <div style={{ flex: 1 }} />
              <input
                type="number" step="0.001"
                value={v.quantidade ?? ''}
                disabled={somenteLeitura}
                placeholder="—"
                title={valor.length === 1
                  ? `Unico lugar do produto: esta contagem e o proprio Est. Atual dele — mexer aqui muda os dois.`
                  : `Quanto tem em ${v.codigo ?? 'neste lugar'}. Vazio = ninguem contou ainda.`}
                onChange={(e) => editar(v.localizacao_id, 'quantidade', e.target.value)}
                style={ESTILO_CAMPO}
              />
              <input
                type="number" step="0.001"
                value={v.quantidade_minima ?? ''}
                disabled={somenteLeitura}
                placeholder={dicaMinimo}
                title={v.quantidade_minima != null
                  ? `Abaixo disso, repor ${v.codigo ?? 'este lugar'} a partir de outro lugar do produto.`
                  : dicaMinimo === '—'
                    ? `Sem minimo proprio, e o produto tambem nao tem Minimo de Compra definido.`
                    : `Sem minimo proprio: herda o Minimo de Compra do produto (${dicaMinimo}). Digite um numero pra esta prateleira ter o seu.`}
                onChange={(e) => editar(v.localizacao_id, 'quantidade_minima', e.target.value)}
                style={ESTILO_CAMPO}
              />
              {!somenteLeitura && (
                <button type="button" title="Tirar deste lugar" onClick={() => remover(v.localizacao_id)}
                  style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'var(--text-muted)', cursor: 'pointer', marginLeft: 4 }}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* A TRAVA da DA-324, na tela. O backend recusa igual — travar so aqui deixaria outro caminho
          gravar o produto quebrado. Aparece so quando o erro e REAL (2+ lugares, nenhuma marcada). */}
      {semPrincipal && (
        <p className="text-sm" style={{ color: 'var(--danger)', marginBottom: 6 }}>
          Marque qual e a <strong>principal</strong> — e dela que a venda baixa. So voce sabe qual e a vitrine.
        </p>
      )}

      {!somenteLeitura && temEscolhivel && (
        <div className="flex items-center gap-1">
          <Plus size={13} style={{ color: 'var(--text-muted)' }} />
          <div style={{ flex: 1 }}>
            <ComboBox opcoes={listaCombo} value={adicionando} onChange={adicionar} placeholder="Adicionar lugar..." />
          </div>
        </div>
      )}
    </div>
  );
}
